import type { GoalCategory, GoalPreset, StudyDate } from '@aj/core';
import { Button, ChipGroup, Field, HStack, Text, VStack } from '@aj/ui';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { useCoach } from '@aj/coach';

import { useActions } from '../../data/hooks.js';
import { useGoalPresets } from '../../data/goalPresets.js';
import { useAppData } from '../../data/AppDataProvider.js';
import { shiftDate } from '@aj/core';
import { useT } from '../../providers/I18nProvider.js';
import type { MessageKey } from '@aj/i18n';

/**
 * Where you are going — asked in three fields, and no more.
 *
 * Everything else a goal can hold (the outcome, the obstacle, the parts) is
 * absent on purpose. A student who has not started yet cannot answer "what
 * gets in your way" with anything better than "laziness", and a goal broken
 * into chapters before the first day is planning theatre. Those fields arrive
 * later, from the coach, at the moment an actual experience has made the
 * question answerable — see `coachFlows.ts`.
 *
 * What survives here is only what the model cannot compute without: the
 * destination, what kind of thing it is, and when it is due. Twenty seconds.
 */

const CATEGORIES: { id: GoalCategory; key: MessageKey }[] = [
  { id: 'academic', key: 'goal.cat.academic' },
  { id: 'career', key: 'goal.cat.career' },
  { id: 'skill', key: 'goal.cat.skill' },
  { id: 'fitness', key: 'goal.cat.fitness' },
  { id: 'business', key: 'goal.cat.business' },
  { id: 'creative', key: 'goal.cat.creative' },
  { id: 'personal', key: 'goal.cat.personal' },
];

/** Horizons offered as one tap. A student who wants an exact date can still
 *  pick one; most are choosing between "this term" and "this year". */
const HORIZONS: { days: number; key: MessageKey }[] = [
  { days: 90, key: 'goal.in3Months' },
  { days: 180, key: 'goal.in6Months' },
  { days: 365, key: 'goal.inAYear' },
];

export type GoalComposeProps = {
  onDone: () => void;
  onCancel: () => void;
};

export function GoalCompose({ onDone, onCancel }: GoalComposeProps) {
  const t = useT();
  const { today } = useAppData();
  const actions = useActions();
  const coach = useCoach();
  const presets = useGoalPresets();

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<GoalCategory>('academic');
  const [days, setDays] = useState<number>(90);
  const [date, setDate] = useState<StudyDate | ''>('');
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => field.current?.focus(), []);

  const targetDate = date === '' ? shiftDate(today, days) : date;
  const ready = title.trim().length > 0;

  const submit = () => {
    if (!ready) return;
    actions.startGoal({ title: title.trim(), category, targetDate });
    coach.completed('goal.started');
    onDone();
  };

  /**
   * A preset starts the goal outright — it does not fill the form in.
   *
   * There is nothing left to decide: the title, the category and the parts
   * all came with it, and the date is the horizon already selected above.
   * Making someone confirm a choice they just made is a second tap that
   * changes nothing, and the parts can be edited from the goal itself
   * afterwards like any other.
   */
  const start = (preset: GoalPreset) => {
    actions.startGoal({
      title: preset.title,
      category: preset.category,
      targetDate,
      preset: preset.id,
      parts: preset.parts,
    });
    coach.completed('goal.started');
    onDone();
  };

  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') submit();
    if (event.key === 'Escape') onCancel();
  };

  return (
    <VStack gap={5} data-coach="goal-compose">
      {/* Above the blank field, because the whole point is to not face one.
          A goal already broken into its chapters is the single most valuable
          thing this app can pre-load: the parts are what progress is measured
          in, and a student who has to type twenty-two of them will start a
          goal with none. Renders nothing at all when there are no packs for
          this student, which is every student today — see `goalPresets.ts`. */}
      {presets.length > 0 && (
        <VStack gap={2}>
          <Text variant="label" tone="ink">
            {t('goal.presets')}
          </Text>
          <ChipGroup
            label={t('goal.presets')}
            options={presets.map((preset) => ({ value: preset.id, label: preset.title }))}
            onSelect={(id) => {
              const preset = presets.find((item) => item.id === id);
              if (preset) start(preset);
            }}
          />
        </VStack>
      )}

      <VStack gap={2}>
        <Text variant="label" tone="ink">
          {t('goal.whatToward')}
        </Text>
        <Field
          inputRef={field}
          value={title}
          onChange={setTitle}
          onKeyDown={keys}
          label={t('goal.whatToward')}
          placeholder={t('goal.titlePlaceholder')}
          className="w-full"
        />
      </VStack>

      {/* Category is one tap and never typed. It shapes copy and the preset
          list later; it is deliberately not part of any score. Not clearable:
          every goal has a category, and an empty one has nothing to show for
          being empty. */}
      <ChipGroup
        label={t('goal.whatToward')}
        options={CATEGORIES.map((option) => ({ value: option.id, label: t(option.key) }))}
        value={category}
        onSelect={(id) => setCategory(id as GoalCategory)}
      />

      <VStack gap={2}>
        <Text variant="label" tone="ink">
          {t('goal.byWhen')}
        </Text>
        <HStack gap={2} className="flex-wrap">
          <ChipGroup
            label={t('goal.byWhen')}
            options={HORIZONS.map((horizon) => ({
              value: String(horizon.days),
              label: t(horizon.key),
            }))}
            value={date === '' ? String(days) : null}
            onSelect={(value) => {
              setDays(Number(value));
              setDate('');
            }}
          />
          <Field
            date
            size="sm"
            value={date}
            onChange={(value) => setDate(value as StudyDate)}
            min={shiftDate(today, 1)}
            label={t('goal.pickDate')}
          />
        </HStack>
      </VStack>

      <HStack gap={2} justify="end">
        <Button variant="ghost" onClick={onCancel}>
          {t('action.cancel')}
        </Button>
        <Button onClick={submit} disabled={!ready}>
          {t('goal.start')}
        </Button>
      </HStack>
    </VStack>
  );
}
